// shared/src/schemas/macro-prediction.ts
// Zod schema for `macro_predictions` table — narrow + JSON details shape
// per docs/2026-05-25-flex-schema-brief-revision.md §K.1 (migration 0020).
//
// One row per M/P-stage thesis emission per brief, discriminated by `path`
// (contrarian / consensus / synthesis) and ordinal `prediction_index`
// within (brief, path).
//
// SCORING contract cols (indexed; scorer + calibration filter/aggregate):
//   prediction_mode · catalyst_date · catalyst_date_ci_low_days · _high_days
//   · post_catalyst_window_days · horizon_days · path · prediction_index
//
// LLM-DISPLAY cols → `details` JSON sidecar (parsed when needed by
// downstream readers via JSON.parse):
//   thesis_narrative · catalyst_description · catalyst_source ·
//   catalyst_probability · horizon_ci_low_days · horizon_ci_high_days
//
// emitting_stage is NOT enum-restricted at the DB level (CHECK relaxed by
// migration 0016 to support V2 stage names without future migrations).

import { z } from 'zod';

// `emitting_stage` deliberately kept open as z.string() — Zod-authoritative
// at app layer; CHECK relaxed at DB layer per migration 0016 to support
// V2 stage names (m.contrarian.refine / m.synthesis / p.contrarian.refine
// etc.) without a future migration.
export const MacroPredictionEmittingStageEnum = z.enum(['m.1', 'm.3']);  // legacy V1 alias; new code uses z.string() directly
export const MacroPredictionModeEnum          = z.enum(['catalyst', 'trend']);

// §K.1 / migration 0020: `prediction_kind` renamed to `path`; 'synthesis'
// added for the V2 third-path adjudicator.
export const MacroPredictionPathEnum = z.enum(['contrarian', 'consensus', 'synthesis']);

export const MacroPredictionSchema = z.object({
  id:                         z.string(),
  brief_id:                   z.string(),
  emitting_stage:             z.string(),                                  // Zod-authoritative; relaxed at DB layer
  path:                       MacroPredictionPathEnum,
  prediction_index:           z.number().int().nonnegative(),
  prediction_mode:            MacroPredictionModeEnum,
  catalyst_date:              z.string().nullable(),                       // ISO 'YYYY-MM-DD' or null (trend mode)
  catalyst_date_ci_low_days:  z.number().int().nullable(),
  catalyst_date_ci_high_days: z.number().int().nullable(),
  post_catalyst_window_days:  z.number().int().nullable(),
  horizon_days:               z.number().int().nullable(),                 // null in catalyst mode
  // R4 audit M-013 (2026-05-28): 175/175 prod rows = DEFAULT 2. No writer
  // ever sets it; no reader filters on it. Kept for forward-bump when the
  // next macro_predictions shape change lands (bump to 3 + propagate through
  // writers per [[feedback-no-tech-debt-pre-launch]] + writer/DDL lockstep).
  // Drop-column migration deferred — column is harmless and a future V3
  // change is more useful than a cleanup churn.
  schema_version:             z.number().int(),
  created_at:                 z.string().datetime(),
  details:                    z.string(),                                  // JSON; parsed by callers when display fields needed
});

export type MacroPrediction = z.infer<typeof MacroPredictionSchema>;
